import { useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import {
  createTransferDestination,
  deleteTransferDestination,
  listTransferDestinations,
  transferDestinationErrorMessage,
  updateTransferDestination,
  type TransferDestination,
} from "../api/transferDestinations";

type Routing = TransferDestination["routing"];

/**
 * Operator-facing copy never says "pstn" or "sip" — those are our wire values
 * (what the backend stores and later uses to pick a dial-string builder), not
 * words an operator should have to learn. An agent mid-call just needs to
 * know "this button dials a phone number" vs "this button dials a SIP
 * address" — see Task 0.4's DESIGN note.
 */
const ROUTING_META: Record<
  Routing,
  { optionLabel: string; fieldLabel: string; placeholder: string; hint: string }
> = {
  pstn: {
    optionLabel: "Phone number",
    fieldLabel: "Phone number",
    placeholder: "(888) 555-0142",
    hint:
      "A regular number, the kind you'd dial on any phone. Punctuation is fine — " +
      "we'll clean it up when you save.",
  },
  sip: {
    optionLabel: "SIP address",
    fieldLabel: "SIP address",
    placeholder: "sales@buyer.example.com",
    hint:
      "Routes straight over the internet instead of a phone network — like an " +
      "email address for calls. Needs a user and a host, e.g. sales@buyer.example.com.",
  },
};

/** PSTN targets are stored as bare digits; show them the way an operator
 * would expect to read a phone number back. SIP targets are shown as-is —
 * mangling anything after the '@' would misrepresent what's actually dialed. */
function formatDisplayTarget(d: TransferDestination): string {
  if (d.routing === "sip") return d.target;
  const digits = d.target.replace(/\D/g, "");
  if (digits.length === 11 && digits.startsWith("1")) {
    return `+1 (${digits.slice(1, 4)}) ${digits.slice(4, 7)}-${digits.slice(7)}`;
  }
  if (digits.length === 10) {
    return `(${digits.slice(0, 3)}) ${digits.slice(3, 6)}-${digits.slice(6)}`;
  }
  return d.target;
}

function AddDestinationModal({
  onClose,
  onCreated,
}: {
  onClose: () => void;
  onCreated: () => void;
}) {
  const [name, setName] = useState("");
  const [routing, setRouting] = useState<Routing>("pstn");
  const [target, setTarget] = useState("");
  const [callerId, setCallerId] = useState("");
  const meta = ROUTING_META[routing];

  const create = useMutation({
    mutationFn: () =>
      createTransferDestination({
        name: name.trim(),
        target: target.trim(),
        routing,
        caller_id: callerId.trim() || null,
      }),
    onSuccess: onCreated,
  });

  return (
    <div className="fixed inset-0 bg-black/30 flex items-center justify-center z-10 p-4">
      <div className="bg-white rounded-lg shadow-xl p-6 w-full max-w-md space-y-4">
        <div>
          <h2 className="text-lg font-bold">Add a transfer destination</h2>
          <p className="text-sm text-gray-500 mt-1">
            This becomes a button your agents can press mid-call to bring this buyer in
            while the customer waits on hold.
          </p>
        </div>

        <div>
          <label htmlFor="td-name" className="block text-xs text-gray-500 mb-1">
            Name
          </label>
          <input
            id="td-name"
            className="w-full border border-gray-300 rounded px-3 py-2 text-sm"
            placeholder="Acme Buyers"
            value={name}
            maxLength={128}
            onChange={(e) => setName(e.target.value)}
          />
          <p className="text-[11px] text-gray-400 mt-1">
            What your agents will see on the button — keep it recognizable.
          </p>
        </div>

        <div>
          <label htmlFor="td-routing" className="block text-xs text-gray-500 mb-1">
            How do you reach them?
          </label>
          <select
            id="td-routing"
            className="w-full border border-gray-300 rounded px-3 py-2 text-sm bg-white"
            value={routing}
            onChange={(e) => {
              setRouting(e.target.value as Routing);
              setTarget("");
            }}
          >
            <option value="pstn">{ROUTING_META.pstn.optionLabel}</option>
            <option value="sip">{ROUTING_META.sip.optionLabel}</option>
          </select>
        </div>

        <div>
          <label htmlFor="td-target" className="block text-xs text-gray-500 mb-1">
            {meta.fieldLabel}
          </label>
          <input
            id="td-target"
            className="w-full border border-gray-300 rounded px-3 py-2 text-sm font-mono"
            placeholder={meta.placeholder}
            value={target}
            maxLength={255}
            onChange={(e) => setTarget(e.target.value)}
          />
          <p className="text-[11px] text-gray-400 mt-1">{meta.hint}</p>
        </div>

        <div>
          <label htmlFor="td-caller-id" className="block text-xs text-gray-500 mb-1">
            Caller ID to show them <span className="text-gray-400">(optional)</span>
          </label>
          <input
            id="td-caller-id"
            className="w-full border border-gray-300 rounded px-3 py-2 text-sm font-mono"
            placeholder="(888) 555-0100"
            value={callerId}
            maxLength={32}
            onChange={(e) => setCallerId(e.target.value)}
          />
          <p className="text-[11px] text-gray-400 mt-1">
            Leave this blank to show the customer's own number — that's what buyers
            usually expect to see. Only set this if you have a number you're allowed to
            present.
          </p>
        </div>

        {create.isError && (
          <p role="alert" className="text-sm text-red-600">
            {transferDestinationErrorMessage(create.error)}
          </p>
        )}

        <div className="flex gap-2 pt-1">
          <button
            disabled={create.isPending || !name.trim() || !target.trim()}
            onClick={() => create.mutate()}
            className="flex-1 bg-brand hover:bg-brand-dark text-white rounded py-2 text-sm disabled:opacity-50"
          >
            {create.isPending ? "Saving…" : "Save"}
          </button>
          <button
            onClick={onClose}
            className="flex-1 bg-gray-100 hover:bg-gray-200 rounded py-2 text-sm"
          >
            Cancel
          </button>
        </div>
      </div>
    </div>
  );
}

export function TransferDestinationsPage() {
  const qc = useQueryClient();
  const [showAdd, setShowAdd] = useState(false);
  const [actionError, setActionError] = useState<string | null>(null);

  const { data, isLoading, isError, error } = useQuery({
    queryKey: ["transfer-destinations"],
    queryFn: listTransferDestinations,
  });

  const invalidate = () => qc.invalidateQueries({ queryKey: ["transfer-destinations"] });

  const toggleActive = useMutation({
    mutationFn: (d: TransferDestination) =>
      updateTransferDestination(d.transfer_destination_id, { is_active: !d.is_active }),
    onSuccess: () => {
      setActionError(null);
      void invalidate();
    },
    onError: (err) => setActionError(transferDestinationErrorMessage(err)),
  });

  const remove = useMutation({
    mutationFn: (id: number) => deleteTransferDestination(id),
    onSuccess: () => {
      setActionError(null);
      void invalidate();
    },
    onError: (err) => setActionError(transferDestinationErrorMessage(err)),
  });

  function handleRemove(d: TransferDestination) {
    if (
      !confirm(
        `Remove "${d.name}"? Your agents will no longer be able to warm-transfer a call ` +
          `to this buyer.`,
      )
    ) {
      return;
    }
    remove.mutate(d.transfer_destination_id);
  }

  const destinations = data ?? [];

  return (
    <div className="max-w-4xl">
      <div className="flex items-end justify-between mb-4">
        <div>
          <h1 className="text-2xl font-bold">Transfer destinations</h1>
          <p className="text-sm text-gray-500 mt-1 max-w-2xl">
            The buyers your agents can loop into a live call. Add one for each buyer you
            sell to and it shows up as a button in the agent console — the agent puts the
            customer on hold, calls the buyer privately, then brings everyone together.
          </p>
        </div>
        <button
          onClick={() => setShowAdd(true)}
          className="bg-brand hover:bg-brand-dark text-white rounded px-4 py-2 text-sm shrink-0"
        >
          Add destination
        </button>
      </div>

      {actionError && (
        <p role="alert" className="mb-3 text-sm text-red-600">
          {actionError}
        </p>
      )}

      {isLoading ? (
        <p className="text-gray-500">Loading…</p>
      ) : isError ? (
        <p className="text-red-600">{transferDestinationErrorMessage(error)}</p>
      ) : destinations.length === 0 ? (
        <div className="bg-white rounded-lg shadow p-8 text-center">
          <p className="font-medium">No transfer destinations yet</p>
          <p className="text-sm text-gray-500 mt-1 mb-4 max-w-md mx-auto">
            Add the buyers you sell calls to. Each one becomes a button an agent can press
            mid-call to put the customer on hold, call the buyer, and bring everyone
            together — without ever losing the customer.
          </p>
          <button
            onClick={() => setShowAdd(true)}
            className="bg-brand hover:bg-brand-dark text-white rounded px-4 py-2 text-sm"
          >
            Add destination
          </button>
        </div>
      ) : (
        <div className="bg-white rounded-lg shadow overflow-hidden">
          <table className="w-full text-sm">
            <thead className="bg-gray-50 text-left text-gray-500">
              <tr>
                <th className="px-4 py-2">Name</th>
                <th className="px-4 py-2">Number or address</th>
                <th className="px-4 py-2">Caller ID shown</th>
                <th className="px-4 py-2">Status</th>
                <th className="px-4 py-2"></th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100">
              {destinations.map((d) => (
                <tr key={d.transfer_destination_id}>
                  <td className="px-4 py-2 font-semibold align-top">
                    {d.name}
                    {d.allowed_agent_ids.length > 0 && (
                      <p className="text-[11px] font-normal text-gray-400">
                        {d.allowed_agent_ids.length} agent
                        {d.allowed_agent_ids.length === 1 ? "" : "s"} only
                      </p>
                    )}
                  </td>
                  <td className="px-4 py-2 align-top">
                    <div className="font-mono">{formatDisplayTarget(d)}</div>
                    <span className="inline-block mt-0.5 px-1.5 py-0.5 rounded text-[10px] bg-gray-100 text-gray-600">
                      {ROUTING_META[d.routing].optionLabel}
                    </span>
                  </td>
                  <td className="px-4 py-2 text-gray-600 align-top">
                    {d.caller_id ? (
                      <span className="font-mono">{d.caller_id}</span>
                    ) : (
                      <span className="text-gray-400 italic">Customer's own number</span>
                    )}
                  </td>
                  <td className="px-4 py-2 align-top">
                    <span
                      className={`px-2 py-0.5 rounded text-xs ${
                        d.is_active
                          ? "bg-green-100 text-green-800"
                          : "bg-gray-200 text-gray-600"
                      }`}
                    >
                      {d.is_active ? "Active" : "Paused"}
                    </span>
                  </td>
                  <td className="px-4 py-2 text-right whitespace-nowrap align-top">
                    <button
                      disabled={toggleActive.isPending}
                      onClick={() => toggleActive.mutate(d)}
                      className="text-brand hover:underline text-sm disabled:opacity-50 mr-3"
                    >
                      {d.is_active ? "Pause" : "Resume"}
                    </button>
                    <button
                      disabled={remove.isPending}
                      onClick={() => handleRemove(d)}
                      className="text-red-600 hover:underline text-sm disabled:opacity-50"
                    >
                      Remove
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {showAdd && (
        <AddDestinationModal
          onClose={() => setShowAdd(false)}
          onCreated={() => {
            setShowAdd(false);
            void invalidate();
          }}
        />
      )}
    </div>
  );
}
